export type EpistemicClass =
  | "observed_fact"
  | "agent_claim"
  | "system_inference"
  | "human_annotation";

export type SourceDescriptor = {
  kind: "codex_app_server" | "experiment_collector" | "memory_mcp" | "system";
  version: string;
};

export type UiEvent<T = unknown> = {
  seq: number;
  eventId: string;
  type: string;
  occurredAt: string;
  observedAt: string;
  source: SourceDescriptor;
  epistemicClass: EpistemicClass;
  threadId?: string;
  turnId?: string;
  itemId?: string;
  payload: T;
};

export type NewUiEvent<T = unknown> = Omit<UiEvent<T>, "seq">;

export type TerminalSessionStatus = "running" | "exited";

export type TerminalSessionView = {
  id: string;
  title: string;
  cwd: string;
  shell: string;
  pid: number;
  status: TerminalSessionStatus;
  attached: boolean;
  createdAt: string;
  lastAttachedAt: string | null;
  exitedAt: string | null;
  exitCode: number | null;
  exitSignal: number | null;
};

export type TerminalSessionListPayload = {
  sessions: TerminalSessionView[];
  limits: {
    maxSessions: number;
    detachTimeoutSeconds: number;
    outputBufferBytes: number;
  };
};

export type TerminalAttachTicket = {
  ticket: string;
  expiresAt: string;
};

export const TERMINAL_PROTOCOL_VERSION = 1 as const;

export type TerminalClientMessage =
  | { type: "auth"; ticket: string; protocolVersion: typeof TERMINAL_PROTOCOL_VERSION }
  | { type: "input"; data: string }
  | { type: "resize"; cols: number; rows: number };

export type TerminalServerMessage =
  | {
    type: "ready";
    protocolVersion: typeof TERMINAL_PROTOCOL_VERSION;
    replayThroughSequence: number;
    session: TerminalSessionView;
  }
  | { type: "output"; sequence: number; data: string }
  | { type: "replay-complete"; sequence: number }
  | { type: "exit"; exitCode: number; signal: number | null }
  | { type: "error"; code: string; message: string };

export type CodexAuthState =
  | "authenticated"
  | "signedOut"
  | "signingIn"
  | "notRequired"
  | "unavailable"
  | "unsupported";

export type CodexAuthAccountView = {
  type: string;
  label: string;
  email: string | null;
  planType: string | null;
};

export type CodexProviderView = {
  id: string;
  label: string;
  kind: "openai" | "relay" | "custom" | "unknown";
  baseUrl: string | null;
};

export type CodexRateLimitWindowView = {
  usedPercent: number;
  remainingPercent: number;
  windowDurationMins: number | null;
  resetsAt: string | null;
};

export type CodexRateLimitBucketView = {
  id: string;
  name: string | null;
  planType: string | null;
  primary: CodexRateLimitWindowView | null;
  secondary: CodexRateLimitWindowView | null;
  credits: {
    hasCredits: boolean;
    unlimited: boolean;
    balance: string | null;
  } | null;
  individualLimit: {
    limit: string;
    used: string;
    remainingPercent: number;
    resetsAt: string | null;
  } | null;
  reachedType: string | null;
};

export type CodexRateLimitsView = {
  buckets: CodexRateLimitBucketView[];
  resetCreditsAvailable: number | null;
  checkedAt: string;
};

export type CodexAccountUsageView = {
  summary: {
    lifetimeTokens: number | null;
    peakDailyTokens: number | null;
    longestRunningTurnSec: number | null;
    currentStreakDays: number | null;
    longestStreakDays: number | null;
  };
  latestDaily: {
    startDate: string;
    tokens: number;
  } | null;
  checkedAt: string;
};

export type CodexDeviceLoginView = {
  method: "deviceCode";
  verificationUrl: string;
  userCode: string;
  startedAt: string;
};

export type CodexAuthView = {
  state: CodexAuthState;
  account: CodexAuthAccountView | null;
  provider: CodexProviderView;
  rateLimits: CodexRateLimitsView | null;
  usage: CodexAccountUsageView | null;
  requiresOpenaiAuth: boolean | null;
  pending: CodexDeviceLoginView | null;
  apiKeyLoginAllowed: boolean;
  message: string | null;
  checkedAt: string;
};

export type CodexApiKeyProviderInput =
  | { mode: "current" }
  | { mode: "openai" }
  | { mode: "relay"; baseUrl: string };

export type RuntimeState =
  | "notLoaded"
  | "idle"
  | "active"
  | "systemError"
  | "unknown";

export type ThreadProjection = {
  id: string;
  sessionId: string | null;
  name: string | null;
  preview: string | null;
  cwd: string;
  sourceKind: string;
  runtimeState: RuntimeState;
  activeFlags: string[];
  parentThreadId: string | null;
  agentRole: string | null;
  agentNickname: string | null;
  createdAt: string;
  updatedAt: string;
  archived: boolean;
  lastSeenAt: string;
  sourceVersion: string;
};

export type ThreadPreference = {
  threadId: string;
  hidden: boolean;
  updatedAt: string;
};

export type ResearchTopicProjection = {
  id: string;
  rootThreadId: string;
  title: string;
  preview: string | null;
  threadIds: string[];
  runtimeState: RuntimeState;
  activeThreadCount: number;
  errorThreadCount: number;
  researchState: ResearchLifecycleState;
  completionId: string | null;
  paperStatus: ResearchPaperStatus;
  updatedAt: string;
  archived: boolean;
  hidden: boolean;
};

export type ResearchLifecycleState =
  | "active"
  | "paused"
  | "blocked"
  | "finalizing"
  | "complete"
  | "reopened";

export type ResearchCompletionTrigger = "goal" | "bounded-turn" | "human" | "agent";

export type ResearchCompletion = {
  completionId: string;
  rootThreadId: string;
  cycle: number;
  trigger: ResearchCompletionTrigger;
  triggerEventId: string | null;
  requirementRevision: number;
  requirementSha256: string;
  terminalTurnId: string;
  experimentIds: string[];
  runIds: string[];
  artifactIds: string[];
  reportIds: string[];
  manifestPath: string;
  manifestSha256: string;
  completedAt: string;
};

export type ResearchCompletionState = {
  rootThreadId: string;
  state: ResearchLifecycleState;
  cycle: number;
  currentCompletionId: string | null;
  requestedAt: string | null;
  requestedTrigger: ResearchCompletionTrigger | null;
  requestedTriggerEventId: string | null;
  requestedTerminalTurnId: string | null;
  updatedAt: string;
  lastError: string | null;
};

export type ResearchPaperStatus =
  | "not_requested"
  | "queued"
  | "writing"
  | "building"
  | "needs_input"
  | "blocked"
  | "ready"
  | "failed"
  | "cancelled";

export type ResearchPaperJob = {
  id: string;
  completionId: string;
  rootThreadId: string;
  templateId: string;
  writingProfile: string;
  writingProfileSha256: string;
  status: Exclude<ResearchPaperStatus, "not_requested">;
  writerThreadId: string | null;
  writerTurnId: string | null;
  writerAttempt?: number;
  operationId: string;
  workPath: string;
  paperId: string | null;
  requestedAt: string;
  writerCompletedAt: string | null;
  retryAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ResearchPaperVersion = {
  paperId: string;
  jobId: string;
  completionId: string;
  rootThreadId: string;
  version: number;
  title: string;
  templateId: string;
  writingProfile: string;
  writingProfileSha256: string;
  publishedAt: string;
  descriptorPath: string;
  pdfPath: string;
  pdfSha256: string;
  pdfSizeBytes: number;
  sourceZipPath: string;
  sourceZipSha256: string;
  buildLogPath: string;
  buildLogSha256: string;
};

export type ResearchReportSummary = {
  reportId: string;
  schemaVersion: 1 | 2;
  seriesId: string | null;
  version: number | null;
  kind: "interim" | "final";
  title: string;
  summary: string;
  rootThreadId: string | null;
  authorThreadId: string | null;
  experimentIds: string[];
  runIds: string[];
  coverageThrough: string;
  publishedAt: string;
  updateIntervalMinutes: number | null;
  nextUpdateDueAt: string | null;
  supersedesReportId: string | null;
};

export type ResearchReportScheduleStatus = "idle" | "running" | "publishing" | "failed" | "final";

export type ResearchReportSchedule = {
  rootThreadId: string;
  seriesId: string;
  title: string;
  requirementPath: string | null;
  enabled: boolean;
  status: ResearchReportScheduleStatus;
  updateIntervalMinutes: number;
  nextUpdateDueAt: string | null;
  latestReportId: string | null;
  sourceReportId: string | null;
  writerThreadId: string | null;
  writerTurnId: string | null;
  operationId: string | null;
  trigger: "manual" | "schedule" | null;
  deliveryMethod: "ephemeral-fork" | "ephemeral-task" | null;
  requestedAt: string | null;
  writerCompletedAt: string | null;
  retryAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ResearchReportDetail = ResearchReportSummary & {
  document: {
    path: string;
    mediaType: "text/markdown";
    sha256: string;
    sizeBytes: number;
  };
  markdown: string;
};

export type TurnProjection = {
  id: string;
  threadId: string;
  status: "inProgress" | "completed" | "interrupted" | "failed" | "unknown";
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  error: string | null;
};

export type ItemProjection = {
  id: string;
  threadId: string;
  turnId: string;
  type: string;
  status: string;
  label: string;
  summary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  details: Record<string, unknown>;
};

export type AgentSpan = {
  id: string;
  threadId: string;
  turnId?: string;
  itemId?: string;
  parentSpanId?: string;
  agentRole?: string | null;
  kind: string;
  label: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  source: string;
  epistemicClass: EpistemicClass;
};

export type FirstObservedLocation = {
  hostId: string;
  hostname: string;
  experimentRoot: string;
  observedAt: string;
};

export type ResponsibleSession = {
  sessionId: string;
  threadId: string;
  rootThreadId: string;
  role: "creator" | "contributor";
};

export type ExperimentProgressStatus =
  | "planned"
  | "running"
  | "completed"
  | "blocked"
  | "cancelled";

export type ExperimentProgressMetric = {
  name: string;
  value: number;
  display: string;
};

export type ExperimentProgressEntry = {
  id: string;
  title: string;
  status: ExperimentProgressStatus;
  description: string;
  result: string | null;
  metrics: ExperimentProgressMetric[];
  references: string[];
  updatedAt: string;
};

export type ExperimentProgressProjection = {
  schema:
    | "roboscientist.experiment_progress.v1"
    | "roboscientist.experiment_progress.v2";
  experimentId: string;
  title: string;
  summary: string;
  progress: ExperimentProgressEntry[];
  updatedAt: string;
};

export type ExperimentRepositoryReference =
  | { kind: "registered"; repositoryId: string }
  | { kind: "path"; repositoryRoot: string };

export type ExperimentRepositoryEntry = ExperimentRepositoryReference & {
  firstUsedAt: string;
  firstUsedByThreadId: string | null;
};

export type ExperimentRepositorySelection = {
  operationId: string;
  repository: ExperimentRepositoryReference;
  selectedAt: string;
  actorThreadId: string | null;
  reason: string;
  changed: boolean | null;
};

export type ExperimentRepositoryScope = {
  mode: "locked" | "extendable";
  repositories: ExperimentRepositoryEntry[];
  activeRepository: ExperimentRepositoryReference | null;
  /** Populated only when projecting legacy v4 manifests. */
  selectionHistory: ExperimentRepositorySelection[];
  activeSelection: ExperimentRepositorySelection | null;
};

export type ExperimentProjection = {
  id: string;
  name: string;
  rootPath: string;
  repositoryId: string | null;
  /** Present for v4/v5 manifests; optional keeps cached/legacy projections compatible. */
  repositoryScope?: ExperimentRepositoryScope | null;
  question: string | null;
  archived: boolean;
  runCount: number;
  sessionCount: number;
  resolvedSessionCount: number;
  pendingSessionCount: number;
  shared: boolean;
  provenanceManaged: boolean;
  provenanceWarning: string | null;
  primaryTopicId: string | null;
  firstObservedLocation: FirstObservedLocation | null;
  responsibleSessions: ResponsibleSession[];
  progress: ExperimentProgressProjection | null;
  progressWarning: string | null;
  updatedAt: string;
};

export type ExperimentSessionLink = {
  experimentId: string;
  linkKey: string;
  sessionId: string | null;
  rootThreadId: string | null;
  attachedByThreadId: string;
  attachedAt: string;
  role: "creator" | "contributor";
  resolutionState: "resolved" | "pending" | "invalid";
  epistemicClass: EpistemicClass;
  rationale: string | null;
  updatedAt: string;
};

export type ExperimentTopicLink = {
  experimentId: string;
  topicId: string | null;
  state: "suggested" | "confirmed" | "dismissed";
  epistemicClass: EpistemicClass;
  rationale: string | null;
  updatedAt: string;
};

export type ResearchNodeRef = {
  proposalId: string;
  experimentId: string;
  nodeId: string;
};

export type ResearchActorRef = {
  threadId: string | null;
  turnId: string | null;
};

export type ResearchEvidenceRef = {
  kind: "run" | "artifact" | "report" | "external";
  experimentId: string | null;
  runId: string | null;
  path: string | null;
  reportId: string | null;
  uri: string | null;
  label: string | null;
};

export type ResearchDecisionRule = {
  id: string;
  statement: string;
  structuredCriteria: Record<string, unknown> | null;
  supersedesRuleId: string | null;
  revisionReason: string | null;
  authoredBy: ResearchActorRef;
  authoredAt: string;
};

export type ResearchAgentAssessment = {
  id: string;
  outcome: "passed" | "failed" | "improved" | "not_improved" | "inconclusive" | "invalid";
  rationale: string;
  evidenceRefs: ResearchEvidenceRef[];
  assessedBy: ResearchActorRef;
  assessedAt: string;
};

export type ResearchSelectionEvent = {
  id: string;
  state: "active" | "frontier" | "champion" | "deprioritized" | "stopped";
  rationale: string;
  selectedBy: ResearchActorRef;
  selectedAt: string;
};

export type ResearchNodeRecord = {
  id: string;
  proposalId: string;
  title: string;
  directionPath: string[];
  hypothesis: string;
  intervention: string;
  primaryParentRef: ResearchNodeRef | null;
  secondaryParentRefs: ResearchNodeRef[];
  comparatorRef: ResearchNodeRef | null;
  decisionRules: ResearchDecisionRule[];
  assessments: ResearchAgentAssessment[];
  selectionHistory: ResearchSelectionEvent[];
  createdBy: ResearchActorRef;
  createdAt: string;
  updatedAt: string;
};

/**
 * A validated v2 fragment owned by one experiment. This is an Observer cache,
 * never a second writable source of research truth.
 */
export type ExperimentResearchStateProjection = {
  schema: "roboscientist.experiment_progress.v2";
  experimentId: string;
  title: string;
  summary: string;
  revision: number;
  nodes: ResearchNodeRecord[];
  legacyProgressCount: number;
  updatedAt: string;
};

export type ResearchLineageRunEvidence = {
  run: RunProjection;
  artifacts: ArtifactProjection[];
  referencedByAssessment: boolean;
  selectedByLatestAssessment: boolean;
};

export type ResearchLineageNodeProjection = ResearchNodeRecord & {
  ref: ResearchNodeRef;
  childRefs: ResearchNodeRef[];
  latestAssessment: ResearchAgentAssessment | null;
  currentSelection: ResearchSelectionEvent | null;
  runEvidence: ResearchLineageRunEvidence[];
};

export type ResearchDirectionProjection = {
  id: string;
  name: string;
  path: string[];
  parentId: string | null;
  childIds: string[];
  nodeRefs: ResearchNodeRef[];
};

export type ResearchLineageWarning = {
  code:
    | "invalid_fragment"
    | "dangling_node_ref"
    | "cross_proposal_ref"
    | "duplicate_node_ref"
    | "lineage_cycle"
    | "orphaned_run_binding";
  message: string;
  nodeRef?: ResearchNodeRef;
  runId?: string;
};

export type ProposalLineageProjection = {
  proposalId: string;
  title: string;
  objective: string | null;
  experiments: Array<{
    experimentId: string;
    title: string;
    summary: string;
    revision: number;
    updatedAt: string;
  }>;
  nodes: ResearchLineageNodeProjection[];
  directions: ResearchDirectionProjection[];
  championRefs: ResearchNodeRef[];
  championLineageRefs: ResearchNodeRef[];
  frontierRefs: ResearchNodeRef[];
  unassignedRuns: RunProjection[];
  orphanedRuns: RunProjection[];
  warnings: ResearchLineageWarning[];
  updatedAt: string;
};

export type ProposalLineagePayload = {
  lineage: ProposalLineageProjection;
};

export type ProposalLineageRevision = Record<string, number>;

export type CandidateExperimentDraft = {
  id: string;
  key: string;
  plannerSessionId: string;
  proposalId: string;
  sourceNodeRef: ResearchNodeRef;
  title: string;
  directionPath: string[];
  hypothesis: string;
  intervention: string;
  decisionRule: string;
  structuredCriteria: Record<string, unknown> | null;
  primaryParentRef: ResearchNodeRef;
  secondaryParentRefs: ResearchNodeRef[];
  comparatorRef: ResearchNodeRef | null;
  rationale: string;
  state: "draft" | "accepted" | "rejected";
  acceptedGuidanceItemId: string | null;
};

export type CandidatePlannerSession = {
  id: string;
  proposalId: string;
  mainThreadId: string;
  plannerThreadId: string;
  sourceNodeRef: ResearchNodeRef;
  sourceDirectionPath: string[];
  lineageRevision: ProposalLineageRevision;
  state: "running" | "ready" | "ended" | "failed";
  summary: string | null;
  error: string | null;
  candidates: CandidateExperimentDraft[];
  chat: CodexChatView;
  createdAt: string;
  updatedAt: string;
};

export type GuidancePriorityV2 = "immediate" | "next_checkpoint" | "passive";
export type GuidanceSourceV2 = "human" | "planner" | "system";
export type GuidanceItemState =
  | "pending"
  | "delivered"
  | "acknowledged"
  | "applied"
  | "rejected"
  | "expired";
export type LineageActionState =
  | "draft"
  | "confirmed"
  | "delivered"
  | "acknowledged"
  | "applied"
  | "needs_input"
  | "stale"
  | "rejected"
  | "expired"
  | "failed";
export type GuidanceDeliveryMethod =
  | "inject"
  | "steer"
  | "start"
  | "watchdog_steer"
  | "watchdog_start";

export type GuidanceDeliveryAttempt = {
  id: string;
  itemId: string;
  method: GuidanceDeliveryMethod;
  status: "accepted" | "failed" | "skipped";
  turnId: string | null;
  attemptedAt: string;
  error: string | null;
};

export type LineageActionView = {
  id: string;
  guidanceItemId: string;
  proposalId: string;
  state: LineageActionState;
  expectedLineageRevision: ProposalLineageRevision;
  attemptCount: number;
  lastAttemptAt: string | null;
  deliveredAt: string | null;
  acknowledgedAt: string | null;
  appliedAt: string | null;
  appliedLineageRevision: Record<string, unknown> | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GuidanceItemView = {
  id: string;
  actionId: string;
  proposalId: string;
  targetDirectionId: string | null;
  targetNodeRef: ResearchNodeRef | null;
  kind:
    | "candidate_accepted"
    | "continue_from_node"
    | "deprioritize_direction"
    | "stop_expanding_direction"
    | "constraint_changed"
    | "evidence_invalidated"
    | "human_correction";
  payload: Record<string, unknown>;
  priority: GuidancePriorityV2;
  createdAt: string;
  expiresAt: string | null;
  maxStalenessSeconds: number | null;
  source: GuidanceSourceV2;
  sourceTurnId: string | null;
  inboxRevision: number;
  state: GuidanceItemState;
  deliveredAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedByThreadId: string | null;
  acknowledgedByTurnId: string | null;
  resolvedAt: string | null;
  resolutionReason: string | null;
  action: LineageActionView;
  deliveryAttempts: GuidanceDeliveryAttempt[];
};

export type GuidanceInboxView = {
  proposalId: string;
  revision: number;
  lastAcknowledgedRevision: number;
  pendingCount: number;
  items: GuidanceItemView[];
  updatedAt: string;
};

export type GuidancePublishInput = {
  kind: GuidanceItemView["kind"];
  payload: Record<string, unknown>;
  priority?: GuidancePriorityV2;
  targetDirectionId?: string | null;
  targetNodeRef?: ResearchNodeRef | null;
  expiresAt?: string | null;
  maxStalenessSeconds?: number | null;
  expectedLineageRevision?: ProposalLineageRevision;
};

export type RunEvidenceProjection = {
  requirement: "required" | "waived" | "not_required" | "unknown";
  status: "complete" | "incomplete" | "corrupt" | "unknown";
  issues: string[];
  reason: string | null;
};

export type RunOutcomeProjection =
  | "satisfied"
  | "unsatisfied"
  | "inconclusive"
  | "not_evaluated";

export type RunProjection = {
  id: string;
  experimentId: string;
  /** Stable run_id declared by run.json; absent for inferred legacy Runs. */
  declaredRunId?: string | null;
  researchNodeRef?: ResearchNodeRef | null;
  unassignedReason?: string | null;
  producer?: {
    name: string;
    contract: string;
    operation: string;
    repositoryId?: string | null;
    frameworkVersion: string | null;
    repositoryRoot: string | null;
    sourceRevision: string | null;
    sourceBranch: string | null;
    sourceDirty: boolean | null;
  } | null;
  phase: string | null;
  kind: "training" | "evaluation" | "diagnostic" | "unknown";
  lifecycle: {
    status: string;
    exitCode: number | null;
  };
  evidence: RunEvidenceProjection;
  outcome: RunOutcomeProjection;
  /** Compatibility mirror of lifecycle.status. */
  status: string;
  path: string;
  startedAt: string | null;
  completedAt: string | null;
  currentStep: number | null;
  maxSteps: number | null;
  progress: {
    unit: string;
    current: number | null;
    total: number | null;
    stage: string | null;
    stageCurrent: number | null;
    stageTotal: number | null;
  };
  etaSeconds: number | null;
  primaryMetricName: string | null;
  primaryMetricValue: number | null;
  semanticProvenance: {
    epistemicClass: "observed_fact" | "system_inference";
    source: "run_manifest" | "observer_projection" | "legacy_adapter";
    rationale: string | null;
  };
  descriptorState: "valid" | "missing" | "invalid" | "stale";
  capabilities: {
    summary: boolean;
    metrics: boolean;
    primaryMetric: boolean;
    metricsDeclared: boolean;
    primaryMetricDeclared: boolean;
    metricsAvailability?: "live" | "terminal" | null;
    metricsDelivery?: "direct" | "hardlink" | "relay" | "copy" | "adapter" | null;
  };
  summary: Record<string, unknown>;
  updatedAt: string;
};

export type MetricPoint = {
  step: number;
  value: number;
  wallTime: string | null;
};

export type MetricSeries = {
  name: string;
  points: MetricPoint[];
};

export type MetricCatalogRun = {
  runId: string;
  metrics: Array<{
    name: string;
    pointCount: number;
  }>;
};

export type ArtifactProjection = {
  id: string;
  runId: string;
  kind: string;
  path: string;
  sizeBytes: number | null;
  sha256: string | null;
};

export type GpuDeviceSnapshot = {
  index: number;
  uuid: string;
  name: string;
  utilizationGpu: number | null;
  utilizationMemory: number | null;
  memoryUsedMiB: number | null;
  memoryTotalMiB: number | null;
  temperatureC: number | null;
  powerDrawW: number | null;
  powerLimitW: number | null;
  processCount: number;
  observedAt: string;
};

export type GpuHistoryPoint = Omit<GpuDeviceSnapshot, "name" | "processCount">;

export type MemoryActor = {
  type: "human" | "agent" | "system" | "unknown";
  id: string | null;
  display_name: string | null;
};

export type MemoryRecording = {
  source_project_id: string | null;
  thread_id: string | null;
  environment_id: string | null;
  channel: string;
  recorder: MemoryActor;
};

export type MemoryContext = {
  project_ids: string[];
  topic_ids: string[];
  experiment_ids: string[];
  run_ids: string[];
  environment_ids: string[];
};

export type MemoryEpistemic = {
  claim_ceiling: "unknown" | "diagnostic" | "development" | "canonical";
};

export type MemoryEvidenceRef = {
  type: "run" | "artifact" | "commit" | "memory" | "external";
  experiment_id: string | null;
  run_id: string | null;
  uri: string | null;
  role: string | null;
  sha256: string | null;
  repository: string | null;
  commit: string | null;
  memory_id: string | null;
};

export type MemoryResolution = {
  system_defaults?: Record<string, unknown>;
  field_sources: Record<string, "system" | "agent" | "agent_override" | "legacy" | "import">;
  agent_overrides: string[];
  agent_cleared: string[];
};

export type MemoryRelation = {
  type: "corrects" | "supersedes";
  target_memory_id: string;
};

export type MemoryEvent = {
  sequence: number;
  event_id: string;
  memory_id: string;
  event_type: "record_created" | "record_imported" | "record_archived";
  payload: Record<string, unknown>;
  occurred_at: string;
};

export type MemoryRecord = {
  schema_version?: number;
  id: string;
  kind: string;
  owner: string;
  content: string;
  recording?: MemoryRecording;
  author?: MemoryActor;
  context?: MemoryContext;
  epistemic?: MemoryEpistemic;
  keywords?: string[];
  evidence_refs?: MemoryEvidenceRef[];
  relations?: MemoryRelation[];
  observed_at?: string | null;
  resolution?: MemoryResolution;
  metadata: Record<string, unknown>;
  created_at: string;
  archived_at: string | null;
};

export type GuidancePriority = "normal" | "urgent";

export type GuidanceView = MemoryRecord & {
  readObservedAt: string | null;
  readEvidenceEventId: string | null;
};

export type MemoryDigestLanguage = "zh-CN" | "en-US";

export type MemoryDigestTrigger = "manual" | "count" | "schedule";

export type MemoryDigestTriggerMode = "manual" | "count" | "schedule" | "hybrid";

export type MemoryDigestJobStatus =
  | "queued"
  | "running"
  | "attention"
  | "succeeded"
  | "failed";

export type MemoryDigestSectionKey =
  | "overall_understanding"
  | "stable_experience"
  | "decision_logic"
  | "failure_boundaries"
  | "cognitive_changes"
  | "open_questions";

export type MemoryDigestParagraph = {
  id: string;
  text: string;
  sourceRefs: string[];
};

export type MemoryDigestSection = {
  key: MemoryDigestSectionKey;
  heading: string;
  paragraphs: MemoryDigestParagraph[];
};

export type MemoryDigestSource = {
  ref: string;
  memoryId: string;
};

export type MemoryDigestView = {
  id: string;
  revision: number;
  language: MemoryDigestLanguage;
  title: string;
  sections: MemoryDigestSection[];
  sources: MemoryDigestSource[];
  sourceFingerprint: string;
  analyzedCount: number;
  citedCount: number;
  generatedThreadId: string;
  reviewedThreadId: string;
  generatedAt: string;
  reviewedAt: string;
  promptVersion: string;
};

export type MemoryDigestJobView = {
  id: string;
  language: MemoryDigestLanguage;
  trigger: MemoryDigestTrigger;
  status: MemoryDigestJobStatus;
  threadId: string | null;
  turnId: string | null;
  sourceFingerprint: string;
  sourceCount: number;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  outcome: "changed" | "unchanged" | null;
  reviewNote: string | null;
  error: string | null;
};

export type MemoryDigestSettings = {
  defaultLanguage: MemoryDigestLanguage;
  triggerMode: MemoryDigestTriggerMode;
  newRecordsThreshold: number;
  scheduleLocalTime: string;
  quietMinutes: number;
  updatedAt: string;
};

export type MemoryDigestSourceState = {
  available: boolean;
  error: string | null;
  activeCount: number;
  analyzedCount: number;
  citedCount: number;
  pendingCount: number;
  changed: boolean;
  limited: boolean;
};

export type MemoryDigestPayload = {
  language: MemoryDigestLanguage;
  digest: MemoryDigestView | null;
  publishedDigests: MemoryDigestView[];
  job: MemoryDigestJobView | null;
  latestJob: MemoryDigestJobView | null;
  availableLanguages: MemoryDigestLanguage[];
  settings: MemoryDigestSettings;
  storage: {
    kind: "sqlite";
    path: string;
    revisionCount: number;
  };
  sourceState: MemoryDigestSourceState;
};

export type MemoryGraphNodeKind = "claim" | "procedure" | "question";
export type MemoryGraphNodeStatus = "active" | "resolved" | "retired";
export type MemoryGraphEvidenceRole = "supports" | "contradicts" | "qualifies" | "raises";
export type MemoryGraphEdgeType = "refines" | "depends_on" | "answers" | "contradicts";

export type MemoryGraphNode = {
  schemaVersion: number;
  id: string;
  kind: MemoryGraphNodeKind;
  title: string;
  statement: string;
  scope: MemoryContext;
  status: MemoryGraphNodeStatus;
  revision: number;
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
};

export type MemoryGraphEvidence = {
  nodeId: string;
  memoryId: string;
  role: MemoryGraphEvidenceRole;
  note: string | null;
  createdRevision: number;
  retiredRevision: number | null;
};

export type MemoryGraphEdge = {
  schemaVersion: number;
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  type: MemoryGraphEdgeType;
  explanation: string;
  evidenceMemoryIds: string[];
  status: "active" | "retired";
  revision: number;
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
};

export type MemoryGraphStatus = {
  schemaVersion: number;
  revision: number;
  baselineSequence: number;
  processedSequence: number;
  memoryHeadSequence: number;
  pendingCount: number;
  activeNodeCount: number;
  activeEdgeCount: number;
  updatedAt: string;
};

export type MemoryGraphDeltaReceipt = {
  operationId: string;
  decision: "applied" | "no_change";
  baseRevision: number;
  revision: number;
  processedSequence: number;
  nodeIds: Record<string, string>;
  edgeIds: Record<string, string>;
  appliedAt: string;
};

export type MemoryGraphSnapshot = {
  status: MemoryGraphStatus;
  nodes: MemoryGraphNode[];
  evidence: MemoryGraphEvidence[];
  edges: MemoryGraphEdge[];
};

export type MemoryGraphJobStatus =
  | "queued"
  | "running"
  | "attention"
  | "succeeded"
  | "failed";

export type MemoryGraphJobView = {
  id: string;
  status: MemoryGraphJobStatus;
  threadId: string | null;
  turnId: string | null;
  baseRevision: number;
  afterSequence: number;
  throughSequence: number;
  sourceCount: number;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  outcome: "changed" | "unchanged" | null;
  resultingRevision: number | null;
  summary: string | null;
  error: string | null;
};

export type MemoryGraphPayload = {
  available: boolean;
  error: string | null;
  status: MemoryGraphStatus | null;
  nodes: MemoryGraphNode[];
  evidence: MemoryGraphEvidence[];
  edges: MemoryGraphEdge[];
  job: MemoryGraphJobView | null;
  latestJob: MemoryGraphJobView | null;
};

export type MemoryImportReport = {
  bundleId: string;
  bundleMode: "full" | "inheritance";
  sourceProjectId: string;
  targetProjectId: string;
  sourceFormat: "portable-bundle" | "cockpit-export";
  sourceLimited: boolean;
  imported: number;
  unchanged: number;
  archivesMerged: number;
  conflicted: number;
};

export type LiteraturePaperStatus = "inbox" | "saved" | "reading" | "dismissed";

export type LiteratureTaskKind = "discovery" | "summary";

export type LiteratureTaskStatus =
  | "queued"
  | "running"
  | "attention"
  | "completed"
  | "failed"
  | "interrupted";

export type LiteratureEvidence = {
  claim: string;
  page: number | null;
  section: string | null;
};

export type LiteraturePaperSummary = {
  overview: string;
  researchQuestion: string;
  contributions: string[];
  method: string;
  results: string[];
  limitations: string[];
  libraryRelevance: string;
  evidence: LiteratureEvidence[];
  generatedAt: string;
};

export type LiteratureMemoryLink = {
  memoryId: string;
  summaryFingerprint: string;
  promotedAt: string;
};

export type LiteratureMemoryState = {
  status: "not_promoted" | "current" | "stale";
  link: LiteratureMemoryLink | null;
};

export type LiteraturePaper = {
  id: string;
  canonicalId: string;
  sourceKind: "remote" | "upload";
  originalFileName: string | null;
  arxivId: string | null;
  title: string;
  authors: string[];
  abstract: string;
  shortSummary: string;
  relevanceReason: string;
  publishedAt: string | null;
  sourceUrl: string;
  pdfUrl: string;
  categories: string[];
  status: LiteraturePaperStatus;
  discoveredAt: string;
  updatedAt: string;
  detailedSummary: LiteraturePaperSummary | null;
  memory: LiteratureMemoryState;
};

export type LiteratureMemoryPromotion = {
  paper: LiteraturePaper;
  record: GuidanceView;
  created: boolean;
  replacedMemoryId: string | null;
};

export type LiteratureTask = {
  id: string;
  kind: LiteratureTaskKind;
  paperId: string | null;
  query: string;
  threadId: string | null;
  turnId: string | null;
  status: LiteratureTaskStatus;
  trigger: "manual" | "scheduled";
  error: string | null;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type LiteratureProfile = {
  query: string;
  cadenceHours: number;
  enabled: boolean;
  scoutThreadId: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  updatedAt: string;
};

export type LiteraturePayload = {
  papers: LiteraturePaper[];
  tasks: LiteratureTask[];
  profile: LiteratureProfile;
  availability: {
    available: boolean;
    message: string | null;
  };
};

export type ComponentHealth = {
  status: "healthy" | "degraded" | "offline" | "incompatible";
  message: string;
  checkedAt: string;
  details?: Record<string, unknown>;
};

export type SystemHealth = {
  overall: "healthy" | "degraded" | "offline";
  codex: ComponentHealth;
  memory: ComponentHealth;
  collector: ComponentHealth;
  resources: ComponentHealth;
  database: ComponentHealth;
};

export type CapabilityMatrix = {
  /** The App Server surface is capability-probed at runtime, not version-gated. */
  codexProtocol?: "app-server-v2";
  /** @deprecated Retained for older clients that still send this display-only field. */
  supportedCodexVersion?: string | null;
  detectedCodexVersion: string | null;
  detectedAppServerVersion: string | null;
  sharedDaemon: boolean;
  liveEvents: boolean;
  interactiveChat: boolean;
  historySnapshots: boolean;
  guidanceWrite: boolean;
  degradedReason: string | null;
};

export type BootstrapPayload = {
  project: {
    id: string;
    name: string;
    workspace: string;
    branch: string | null;
  };
  cursor: number;
  generatedAt: string;
  capabilities: CapabilityMatrix;
  health: SystemHealth;
  threads: ThreadProjection[];
  topics: ResearchTopicProjection[];
  threadPreferences: ThreadPreference[];
  experiments: ExperimentProjection[];
  experimentLinks: ExperimentTopicLink[];
  experimentSessions: ExperimentSessionLink[];
  gpu: GpuDeviceSnapshot[];
  guidance: GuidanceView[];
  repositories: ResearchRepositoryOption[];
  defaultRepositoryId: string | null;
};

export type ResearchRepositoryOption = {
  id: string;
  root: string;
  provider: string;
  isDefault: boolean;
};

export type TopicExperimentsPayload = {
  topic: ResearchTopicProjection;
  requirement: ResearchRequirementProjection | null;
  experiments: ExperimentProjection[];
  links: ExperimentTopicLink[];
  sessionLinks: ExperimentSessionLink[];
  runs: RunProjection[];
  reports: ResearchReportSummary[];
  reportSchedule: ResearchReportSchedule | null;
  completionState: ResearchCompletionState;
  completions: ResearchCompletion[];
  paperJobs: ResearchPaperJob[];
  papers: ResearchPaperVersion[];
};

export const LEGACY_GALLERY_SNAPSHOT_SCHEMA = "roboscientist.gallery_snapshot.v1" as const;
export const LEGACY_GALLERY_SNAPSHOT_V2_SCHEMA = "roboscientist.gallery_snapshot.v2" as const;
export const GALLERY_SNAPSHOT_SCHEMA = "roboscientist.gallery_snapshot.v3" as const;

export type GallerySnapshotMetricSeries = {
  runId: string;
  series: MetricSeries[];
};

/**
 * Immutable, display-only capture of the three Topic-scoped Cockpit views.
 * It intentionally excludes chat state, machine resources, logs, and raw artifacts.
 */
export type GallerySnapshot = {
  schema: typeof GALLERY_SNAPSHOT_SCHEMA;
  snapshot: {
    id: string;
    title: string;
    summary: string;
    exportedAt: string;
    sourceProject: {
      id: string;
      name: string;
    };
  };
  topic: ResearchTopicProjection;
  requirement: ResearchRequirementProjection | null;
  lineage: ProposalLineageProjection;
  experiments: ExperimentProjection[];
  runs: RunProjection[];
  metrics: GallerySnapshotMetricSeries[];
  reports: ResearchReportDetail[];
  completionState: ResearchCompletionState;
  completions: ResearchCompletion[];
  paperJobs: ResearchPaperJob[];
  papers: ResearchPaperVersion[];
};

export type ExperimentSessionsPayload = {
  experiment: ExperimentProjection;
  sessions: ExperimentSessionLink[];
  threads: ThreadProjection[];
  sourceThreadId: string | null;
};

export type GuidanceCreateInput = {
  content: string;
  priority: GuidancePriority;
};

export type FilePreview = {
  path: string;
  sizeBytes: number;
  truncated: boolean;
  mediaType: string;
  content: string | null;
};

export type ProjectionSnapshot = {
  threads: ThreadProjection[];
  turns: TurnProjection[];
  items: ItemProjection[];
  spans: AgentSpan[];
};

export type CodexChatMode = "main" | "side";

const compactCodexChatThreadText = (value: string, maxCharacters: number): string => {
  const normalized = value.replace(/\s+/gu, " ").trim();
  const characters = Array.from(normalized);
  return characters.length > maxCharacters
    ? `${characters.slice(0, maxCharacters - 1).join("")}…`
    : normalized;
};

const codexChatThreadSeed = (message: string, fallback?: string): string =>
  compactCodexChatThreadText(message, 320)
  || compactCodexChatThreadText(fallback ?? "", 320)
  || "New conversation";

export const codexChatPreviewFromMessage = (message: string, fallback?: string): string =>
  codexChatThreadSeed(message, fallback);

export const codexChatTitleFromMessage = (message: string, fallback?: string): string =>
  compactCodexChatThreadText(codexChatThreadSeed(message, fallback), 72);

export type CodexChatMessage = {
  id: string;
  turnId: string | null;
  role: "user" | "assistant" | "system";
  phase: string | null;
  text: string;
  skills: string[];
  mentions: string[];
  status: "streaming" | "complete" | "incomplete" | "failed";
  createdAt: string;
};

export type CodexChatActivity = {
  id: string;
  turnId: string | null;
  type: string;
  label: string;
  summary: string | null;
  output: string | null;
  status: "running" | "completed" | "failed" | "declined";
  startedAt: string | null;
  updatedAt: string;
};

export type CodexChatQuestion = {
  id: string;
  header: string;
  question: string;
  isOther: boolean;
  isSecret: boolean;
  options: Array<{ label: string; description: string }> | null;
};

export type CodexChatRequest = {
  id: string;
  requestId: string | number;
  connectionEpoch: string;
  threadId: string;
  turnId: string | null;
  itemId: string | null;
  kind: "command" | "fileChange" | "permissions" | "userInput" | "mcpElicitation" | "unsupported";
  title: string;
  description: string | null;
  command: string | null;
  cwd: string | null;
  questions: CodexChatQuestion[];
  details: Record<string, unknown>;
  startedAt: string;
};

export type CodexChatPermissionMode =
  | `profile:${string}`
  | "read-only"
  | "ask-for-approval"
  | "approve-for-me"
  | "full-access";

export type CodexChatComposerSettings = {
  permissionMode: CodexChatPermissionMode;
  model: string;
  reasoningEffort: string;
};

export type CodexChatPermissionOption = {
  id: CodexChatPermissionMode;
  label: string;
  description: string;
  profileId: string;
  allowed: boolean;
  dangerous: boolean;
};

export type CodexChatReasoningEffortOption = {
  id: string;
  label: string;
  description: string;
};

export type CodexChatModelOption = {
  id: string;
  displayName: string;
  description: string;
  isDefault: boolean;
  defaultReasoningEffort: string;
  supportedReasoningEfforts: CodexChatReasoningEffortOption[];
};

export type CodexChatCombinationRule = {
  id: string;
  permissionModes: CodexChatPermissionMode[];
  reasoningEfforts: string[];
  disposition: "requiresConfirmation";
  acknowledgement: string;
  title: string;
  description: string;
};

export type CodexChatOptionsView = {
  permissions: CodexChatPermissionOption[];
  models: CodexChatModelOption[];
  combinationRules: CodexChatCombinationRule[];
  defaults: CodexChatComposerSettings;
};

export type CodexChatView = {
  threadId: string;
  mode: CodexChatMode;
  mainThreadId: string;
  forkedFromId: string | null;
  name: string | null;
  preview: string | null;
  cwd: string;
  ephemeral: boolean;
  state: "ready" | "running" | "ended";
  activeTurnId: string | null;
  editableTurnId: string | null;
  editableMessageId: string | null;
  composerSettings: CodexChatComposerSettings;
  connectionEpoch: string;
  history: {
    mode: "paginated" | "live";
    loadedTurns: number;
    hasOlder: boolean;
    oversizedRecovery: boolean;
  };
  messages: CodexChatMessage[];
  activities: CodexChatActivity[];
  pendingRequests: CodexChatRequest[];
  createdAt: string;
  updatedAt: string;
};

export type CodexChatGoalStatus =
  | "active"
  | "paused"
  | "blocked"
  | "usageLimited"
  | "budgetLimited"
  | "complete";

export type CodexChatGoal = {
  threadId: string;
  objective: string;
  status: CodexChatGoalStatus;
  tokenBudget: number | null;
  tokensUsed: number;
  timeUsedSeconds: number;
  createdAt: number;
  updatedAt: number;
};

export type CodexResearchRequirementFile = {
  path: string;
  relativePath: string;
};

export type ResearchRunMode = "bounded" | "persistent-goal" | "continuous-improvement";

export type ResearchPaperPlan = {
  enabled: boolean;
  templateId: string;
  writingProfile: string;
};

export const DEFAULT_RESEARCH_PAPER_PLAN: ResearchPaperPlan = {
  enabled: true,
  templateId: "iclr-2027-conference",
  writingProfile: "write-evidence-bounded-paper"
};

export const DEFAULT_RESEARCH_DELIVERABLES = [
  "Reproducible evidence and an evidence-backed conclusion.",
  "Create code or configuration changes only when required to test or fix an observed gap.",
  "Publish a standalone report only when explicitly requested, needed for long-term handoff, or required by a project protocol."
].join("\n\n");

export const DEFAULT_RESEARCH_STOP_CONDITIONS =
  "Stop when the evaluation and acceptance criteria are satisfied with reproducible evidence and the requested deliverables are complete. Stop earlier and ask for human direction if progress is blocked by missing access or data, an unsafe or irreversible action, or a material ambiguity that would change the research direction.";

export type ResearchRequirementDraft = {
  topic: string;
  objective: string;
  baseline: string;
  evaluation: string;
  notes: string;
  scope: string;
  constraints: string;
  maxGpus: string;
  computeBudget: string;
  computeNotes: string;
  deliverables: string;
  stopConditions: string;
};

export type ResearchRequirementProjection = {
  topicId: string;
  rootThreadId: string;
  experimentId: string | null;
  runMode: ResearchRunMode;
  paperPlan: ResearchPaperPlan;
  draft: ResearchRequirementDraft;
  revision: number;
  sha256: string;
  frozen: true;
  confirmedAt: string;
  sourceFiles: string[];
};

export const createResearchRequirementDraft = (): ResearchRequirementDraft => ({
  topic: "",
  objective: "",
  baseline: "",
  evaluation: "",
  notes: "",
  scope: "",
  constraints: "",
  maxGpus: "",
  computeBudget: "",
  computeNotes: "",
  deliverables: DEFAULT_RESEARCH_DELIVERABLES,
  stopConditions: DEFAULT_RESEARCH_STOP_CONDITIONS
});

const requirementValue = (value: string): string => value.trim() || "NA";
const requirementSection = (title: string, value: string): string =>
  `## ${title}\n\n${requirementValue(value)}`;

export const completeResearchRequirementDraft = (
  draft: ResearchRequirementDraft
): ResearchRequirementDraft => Object.fromEntries(
  Object.entries(draft).map(([key, value]) => [key, requirementValue(value)])
) as ResearchRequirementDraft;

export function buildResearchRequirement(draft: ResearchRequirementDraft): string {
  const complete = completeResearchRequirementDraft(draft);
  return `${[
    `# ${complete.topic.replace(/^#+\s*/, "").replace(/\s+/g, " ").slice(0, 150)}`,
    requirementSection("Topic", complete.topic),
    requirementSection("Objective", complete.objective),
    requirementSection("Scope", complete.scope),
    requirementSection("Constraints", complete.constraints),
    requirementSection("Baseline", complete.baseline),
    requirementSection("Evaluation and acceptance", complete.evaluation),
    requirementSection("Notes", complete.notes),
    requirementSection("Compute and resources", [
      `- Maximum GPUs: ${complete.maxGpus}`,
      `- Runtime / budget: ${complete.computeBudget}`,
      `- Environment / resources: ${complete.computeNotes}`
    ].join("\n")),
    requirementSection("Deliverables", complete.deliverables),
    requirementSection("Stop conditions", complete.stopConditions)
  ].join("\n\n")}\n`;
}

export type ResearchIntakeMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: string;
};

export type ResearchIntakeSession = {
  id: string;
  state: "running" | "ready" | "failed" | "committed" | "closed";
  runMode: ResearchRunMode;
  paperPlan: ResearchPaperPlan;
  draft: ResearchRequirementDraft;
  files: CodexResearchRequirementFile[];
  messages: ResearchIntakeMessage[];
  questions: string[];
  readyForReview: boolean;
  revision: number;
  plannerThreadId: string | null;
  activeTurnId: string | null;
  committedThreadId: string | null;
  experimentId: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ResearchIntakeCreateInput = {
  message: string;
  runMode: ResearchRunMode;
  paperPlan?: ResearchPaperPlan;
  files?: CodexResearchRequirementFile[];
  directEntry?: boolean;
};

export type ResearchIntakeDiscussInput = {
  message: string;
  expectedRevision: number;
  skipClarification?: boolean;
};

export type ResearchIntakeUpdateInput = {
  draft: ResearchRequirementDraft;
  runMode: ResearchRunMode;
  paperPlan: ResearchPaperPlan;
  expectedRevision: number;
};

export type ResearchIntakeCommitInput = ResearchIntakeUpdateInput & {
  repositoryId?: string | null;
  repositoryRoot?: string;
  files?: CodexResearchRequirementFile[];
  model?: string;
  reasoningEffort?: string;
  reportIntervalMinutes?: number;
};

export type CodexResearchStartInput = {
  requirementText?: string;
  topicName?: string;
  experimentId?: string;
  experimentRoot?: string;
  requirementRevision?: number;
  repositoryId?: string | null;
  repositoryRoot?: string;
  files?: CodexResearchRequirementFile[];
  model?: string;
  reasoningEffort?: string;
  goalEnabled?: boolean;
  continuousResearch?: boolean;
  paperPlan?: ResearchPaperPlan;
  reportIntervalMinutes?: number;
};

export type CodexResearchStartResult = {
  chat: CodexChatView;
  goal: CodexChatGoal | null;
  requirementPath: string;
  repositoryId: string | null;
  repositoryRoot: string | null;
  continuousResearch: boolean;
  experimentId?: string | null;
  reportSchedule?: ResearchReportSchedule | null;
};

export type ResearchIntakeCommitResult = CodexResearchStartResult & {
  experimentId: string;
};

export const RESEARCH_REQUIREMENT_TEMPLATE = `# Research requirement

## Topic

Give this research a concise, faithful name.

## Objective

Describe the question to answer or outcome to improve.

## Scope

- In scope:
- Out of scope:

## Constraints

- Safety, compatibility, time, data, or method constraints:

## Baseline

Describe the current baseline and the evidence already available.

## Evaluation and acceptance

- Metrics:
- Comparison protocol:
- Evidence required for acceptance:

## Compute and resources

Describe the available compute, runtime limits, datasets, and external services.

## Deliverables

List the required code, artifacts, results, and report.

## Stop conditions

State when the work is complete and which blockers require human direction.
`;

export type CodexChatRuntimeStatus = "notLoaded" | "idle" | "active" | "systemError" | "unknown";

export type CodexChatStatusView = {
  threadId: string;
  runtimeStatus: CodexChatRuntimeStatus;
  activeFlags: Array<"waitingOnApproval" | "waitingOnUserInput">;
  chatState: CodexChatView["state"];
  activeTurnId: string | null;
  cwd: string;
  model: string | null;
  modelProvider: string | null;
  serviceTier: string | null;
  approvalPolicy: string | null;
  approvalsReviewer: string | null;
  sandbox: string | null;
  reasoningEffort: string | null;
  instructionSources: string[];
  context: {
    usedTokens: number;
    windowTokens: number;
    remainingPercent: number;
  } | null;
  ephemeral: boolean;
  forkedFromId: string | null;
  updatedAt: string;
};

export type CodexChatEditResult = {
  result: "started" | "branchCreated";
  chat: CodexChatView;
  error: string | null;
};

export type CodexSkillView = {
  name: string;
  description: string;
  shortDescription: string | null;
  path: string;
  scope: string;
  enabled: boolean;
};

export type CodexFileMatch = {
  root: string;
  path: string;
  fileName: string;
  score: number;
};

export type CodexUploadedFile = {
  path: string;
  fileName: string;
  size: number;
};
